/**
 * Copyright Mántum All Rights Reserved.
 */
import Component from './component';
import Validator from '../../validator';
import { EVENT_CLEAR, ACTION_VALUE, useGroupDispatch } from '../../../group';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import type { Detail, Option } from '@mantum/kanela';
import type { PropTypes } from './component';
import type { SelectInstance } from 'react-select';

export default forwardRef<Detail<unknown>, PropTypes>(
    function Select(props, ref) {
        const {
            disabled = false,
            error = false,
            name,
            optional = true
        } = props;

        const dispatch = useGroupDispatch();

        const [empty, setEmpty] = useState(false);
        const inputRef = useRef<SelectInstance<Option<unknown>>>(null);

        useImperativeHandle(
            ref,
            () => {
                return {
                    name,
                    validable: !optional,
                    clear() {
                        if (disabled) {
                            return;
                        }

                        setEmpty(false);
                        if (name) {
                            dispatch({
                                type: EVENT_CLEAR,
                                content: [
                                    {
                                        action: ACTION_VALUE,
                                        target: name
                                    }
                                ]
                            });
                        }
                    },
                    validate() {
                        const selected = inputRef.current?.getValue();
                        if (selected?.length === 0) {
                            setEmpty(true);
                            return false;
                        }

                        setEmpty(false);
                        return true;
                    },
                    value() {
                        return inputRef.current?.getValue();
                    },
                    focus(value?: string) {
                        if (name === value) {
                            return inputRef.current?.focus();
                        }
                    }
                };
            },
            [name, optional, dispatch, disabled]
        );

        const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            if (!optional) {
                let value = false;
                const selected = inputRef.current?.getValue();
                if (selected?.length === 0) {
                    value = true;
                }

                setEmpty(value);
            }

            if (props.onBlur) {
                props.onBlur(event);
            }
        };

        return (
            <Validator
                error={empty || error}
                message={props.validation?.message}
                type='select'
            >
                <Component
                    {...props}
                    error={empty || error}
                    onBlur={handleBlur}
                    onChange={props.onChange}
                    onMessage={props.onMessage}
                    ref={inputRef}
                    value={props.value}
                />
            </Validator>
        );
    }
);
